// =====================================================================
// Affichage des montants et des dates.
//
// MONTANTS : francs CFA entiers, jamais de decimales, separateur de
// milliers = une espace. Exemple : 1 719 000 F CFA
//
// Le formatage est fait a la main plutot qu'avec Intl.NumberFormat :
// selon le telephone et la version du navigateur, Intl produit une
// espace insecable etroite (U+202F) ou une espace insecable (U+00A0),
// et l'affichage differerait entre le telephone de Montreal et celui de
// Mbour. Ici, c'est une espace ordinaire partout, toujours.
// =====================================================================

const CURRENCY_SUFFIX = 'F CFA'

/** 1719000 -> "1 719 000" */
export function formatAmount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'

  const rounded = Math.round(value)
  const negative = rounded < 0
  const digits = Math.abs(rounded).toString()

  let grouped = ''
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) grouped += ' '
    grouped += digits[i]
  }
  return negative ? `-${grouped}` : grouped
}

/** 1719000 -> "1 719 000 F CFA" */
export function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return `${formatAmount(value)} ${CURRENCY_SUFFIX}`
}

/**
 * Arrondi au franc entier, au plus proche, la moitie vers le haut.
 * Toute somme d'argent qui passe par un calcul doit passer par ici :
 * il n'y a pas de centimes de franc CFA.
 */
export function toWholeFrancs(value: number): number {
  return Math.round(value)
}

/** "2026-09-05" ou une Date -> "05/09/2026" */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—'
  const d = typeof value === 'string' ? parseIsoDate(value) : value
  if (!d || Number.isNaN(d.getTime())) return '—'
  const day = String(d.getUTCDate()).padStart(2, '0')
  const month = String(d.getUTCMonth() + 1).padStart(2, '0')
  return `${day}/${month}/${d.getUTCFullYear()}`
}

/**
 * Lit une date "AAAA-MM-JJ" sans decalage de fuseau. Indispensable :
 * vous etes a Montreal, Idrissa est a Mbour, et une date de loyer ne
 * doit jamais changer de jour selon qui la regarde.
 */
export function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return null
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
}

// ---------------------------------------------------------------------
// Periodes : du 5 au 5. La periode "2026-09" court du 05/09/2026 au
// 05/10/2026 et le loyer est du le 05/09/2026. Ces fonctions sont le
// miroir exact de period_start() et period_end() dans schema.sql.
// ---------------------------------------------------------------------

const MONTHS_FR = [
  'janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre',
]

const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** "2026-09" -> Date du 05/09/2026 */
export function periodStart(period: string): Date {
  const [year, month] = period.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, 5))
}

/** "2026-09" -> Date du 05/10/2026 */
export function periodEnd(period: string): Date {
  const [year, month] = period.split('-').map(Number)
  return new Date(Date.UTC(year, month, 5))
}

/** Le loyer d'une periode est du le premier jour de cette periode. */
export function periodDueDate(period: string): Date {
  return periodStart(period)
}

/** "2026-09" -> "septembre 2026" / "September 2026" */
export function formatPeriod(period: string, lang: 'fr' | 'en'): string {
  const [year, month] = period.split('-').map(Number)
  if (!year || !month) return period
  const names = lang === 'fr' ? MONTHS_FR : MONTHS_EN
  return `${names[month - 1]} ${year}`
}

/** "2026-09" -> "du 05/09/2026 au 05/10/2026" */
export function formatPeriodRange(period: string, lang: 'fr' | 'en'): string {
  const from = formatDate(periodStart(period))
  const to = formatDate(periodEnd(period))
  return lang === 'fr' ? `du ${from} au ${to}` : `${from} to ${to}`
}

/**
 * Nombre de mois entiers entre deux dates.
 *
 * Compte les mois de calendrier, pas des tranches de trente jours : du
 * 1er mars au 1er septembre il y a six mois, quelle que soit la
 * longueur des mois traverses. Un locataire entre le 5 d'un mois et
 * sort le 4 du mois d'apres n'a pas fait un mois entier.
 */
export function monthsBetween(
  from: string | Date,
  to: string | Date = new Date(),
): number {
  const a = typeof from === 'string' ? parseIsoDate(from) : from
  const b = typeof to === 'string' ? parseIsoDate(to) : to
  if (!a || !b) return 0

  let mois =
    (b.getUTCFullYear() - a.getUTCFullYear()) * 12 +
    (b.getUTCMonth() - a.getUTCMonth())

  // Le mois en cours n'est pas acquis si le jour n'est pas atteint.
  if (b.getUTCDate() < a.getUTCDate()) mois -= 1

  return Math.max(0, mois)
}

/**
 * Duree d'occupation, decoupee en annees et mois.
 *
 * Renvoie null si la date de debut est inconnue : c'est le cas des
 * baux marques a verifier, dont aucun document ne donne les dates. Un
 * zero serait un mensonge, il faut afficher que l'on ne sait pas.
 */
export function occupancyDuration(
  startDate: string | null,
  endDate: string | null,
  today: Date = new Date(),
): { years: number; months: number; totalMonths: number } | null {
  if (!startDate) return null
  // Un bail termine se mesure jusqu'a sa fin, un bail en cours
  // jusqu'a aujourd'hui.
  const fin = endDate && parseIsoDate(endDate) && parseIsoDate(endDate)! < today
    ? endDate
    : today
  const total = monthsBetween(startDate, fin)
  return {
    years: Math.floor(total / 12),
    months: total % 12,
    totalMonths: total,
  }
}

/** Les douze periodes d'une annee, de AAAA-01 a AAAA-12. */
export function periodsOfYear(year: number): string[] {
  return Array.from(
    { length: 12 },
    (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`,
  )
}

/** L'annee portee par une periode. */
export function yearOfPeriod(period: string): number {
  return Number(period.slice(0, 4))
}

/**
 * Mois a afficher par defaut dans une annee donnee : le mois courant
 * s'il tombe dans cette annee, sinon janvier. Ouvrir 2024 sur
 * « septembre » n'aurait aucun sens.
 */
export function defaultPeriodForYear(
  year: number,
  today: Date = new Date(),
): string {
  const courant = currentPeriod(today)
  return yearOfPeriod(courant) === year ? courant : `${year}-01`
}

/** Ramene une periode dans l'annee demandee, en gardant son mois. */
export function clampPeriodToYear(period: string, year: number): string {
  if (yearOfPeriod(period) === year) return period
  return `${year}-${period.slice(5, 7)}`
}

/** Periode courante, selon la regle du 5 au 5. */
export function currentPeriod(today: Date = new Date()): string {
  const year = today.getFullYear()
  const month = today.getMonth() + 1
  // Avant le 5, nous sommes encore dans la periode du mois precedent.
  if (today.getDate() < 5) {
    return month === 1
      ? `${year - 1}-12`
      : `${year}-${String(month - 1).padStart(2, '0')}`
  }
  return `${year}-${String(month).padStart(2, '0')}`
}
